/**
 * Theme Runtime — Apply/clear custom theme at runtime
 *
 * Overrides CSS custom properties on document.documentElement.
 * Works with both hex (--color-*) and HSL (--primary, etc.) variable systems.
 */

import type { CustomThemeColors } from './types';

/**
 * Convert hex color string to HSL string (without "hsl()" wrapper).
 * Returns format: "H S% L%" for Tailwind CSS variable usage.
 */
export function hexToHSL(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '0 0% 0%';

    const r = parseInt(result[1], 16) / 255;
    const g = parseInt(result[2], 16) / 255;
    const b = parseInt(result[3], 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    if (max === min) {
        return `0 0% ${Math.round(l * 100)}%`;
    }

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    let h = 0;
    if (max === r) {
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    } else if (max === g) {
        h = ((b - r) / d + 2) / 6;
    } else {
        h = ((r - g) / d + 4) / 6;
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Darken a hex color by a percentage (0-100).
 */
export function darkenHex(hex: string, percent: number): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;

    const factor = 1 - percent / 100;
    const r = Math.round(parseInt(result[1], 16) * factor);
    const g = Math.round(parseInt(result[2], 16) * factor);
    const b = Math.round(parseInt(result[3], 16) * factor);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Lighten a hex color by mixing with white.
 */
export function lightenHex(hex: string, percent: number): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;

    const factor = percent / 100;
    const r = Math.round(parseInt(result[1], 16) + (255 - parseInt(result[1], 16)) * factor);
    const g = Math.round(parseInt(result[2], 16) + (255 - parseInt(result[2], 16)) * factor);
    const b = Math.round(parseInt(result[3], 16) + (255 - parseInt(result[3], 16)) * factor);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Mapping from CustomThemeColors keys to CSS variable names.
 * Each key maps to hex var (Ant Design) and optionally HSL var (Tailwind/shadcn).
 */
interface VarMapping {
    hex: string;
    hsl?: string;
}

/**
 * Mapping from CustomThemeColors keys to CSS variable names.
 *
 * IMPORTANT: In the design system, the hex and HSL token systems have
 * different naming for the same concept:
 *   - Hex system: --color-primary = indigo (brand), --color-cta = orange (action)
 *   - HSL system: --primary = orange (action, same as CTA)
 *
 * So `cta` must override both --color-cta (hex) AND --primary (HSL),
 * while `primary` overrides only --color-primary (hex).
 */
const COLOR_VAR_MAP: Record<keyof CustomThemeColors, VarMapping> = {
    primary: { hex: '--color-primary' },
    cta: { hex: '--color-cta', hsl: '--primary' },
    background: { hex: '--color-background', hsl: '--background' },
    surface: { hex: '--color-surface', hsl: '--card' },
    text: { hex: '--color-text', hsl: '--foreground' },
    textMuted: { hex: '--color-text-muted', hsl: '--muted-foreground' },
    border: { hex: '--color-border', hsl: '--border' },
    sidebarSelectedBg: { hex: '--color-sidebar-menu-selected-bg' },
    sidebarSelectedText: { hex: '--color-sidebar-menu-selected-text' },
    surfaceElevated: { hex: '--color-surface-elevated', hsl: '--popover' },
    textLight: { hex: '--color-text-light' },
    // EAA-1804: `textOnCta` used to write --color-table-header-text, because
    // the header *was* the CTA bar. Now that the header is neutral, this
    // writes its own token and the header has its own two keys below.
    textOnCta: { hex: '--color-text-on-cta', hsl: '--primary-foreground' },
    tableHeaderBg: { hex: '--color-table-header-bg' },
    tableHeaderText: { hex: '--color-table-header-text' },
    success: { hex: '--color-success', hsl: '--success' },
    warning: { hex: '--color-warning', hsl: '--warning' },
    error: { hex: '--color-error', hsl: '--destructive' },
    info: { hex: '--color-info', hsl: '--info' },
};

/**
 * Derived CSS variables auto-computed from the customizable tokens.
 * Format: [sourceKey, cssVarName, transform]
 */
type DerivedVar = [keyof CustomThemeColors, string, (hex: string) => string];

const DERIVED_HEX_VARS: DerivedVar[] = [
    ['primary', '--color-primary-hover', (h) => darkenHex(h, 12)],
    ['primary', '--color-secondary', (h) => lightenHex(h, 20)],
    ['cta', '--color-cta-hover', (h) => darkenHex(h, 12)],
    // EAA-1804: the header no longer follows `cta` — that coupling is what
    // made every tenant's data tables a solid brand band. Its hover state
    // still derives, but from the header's own color.
    ['tableHeaderBg', '--color-table-header-bg-hover', (h) => darkenHex(h, 3)],
    ['surface', '--color-surface-elevated', (h) => h],
    ['background', '--color-border-light', (h) => lightenHex(h, 5)],
];

const DERIVED_HSL_VARS: [keyof CustomThemeColors, string, (hex: string) => string][] = [
    // Surface derivatives
    ['surface', '--popover', (h) => h],

    // Text derivatives — foreground colors for card, popover, primary, secondary, accent
    ['text', '--card-foreground', (h) => h],
    ['text', '--popover-foreground', (h) => h],

    // Background derivatives
    ['background', '--input', (h) => h],

    // CTA derivatives — in HSL system, --primary = CTA action color
    // So secondary, accent, ring, primary-foreground all derive from CTA
    ['cta', '--ring', (h) => h],
    ['cta', '--secondary', (h) => lightenHex(h, 80)],
    ['cta', '--secondary-foreground', (h) => darkenHex(h, 30)],
    ['cta', '--accent', (h) => lightenHex(h, 85)],
    ['cta', '--accent-foreground', (h) => darkenHex(h, 30)],
    ['cta', '--primary-foreground', () => '#ffffff'],

    // Background derivatives — muted is a slightly tinted background
    ['background', '--muted', (h) => lightenHex(h, 3)],
];

/** Set of all CSS var names managed by the runtime (for cleanup). */
function getAllManagedVars(): string[] {
    const vars: string[] = [];

    for (const mapping of Object.values(COLOR_VAR_MAP)) {
        vars.push(mapping.hex);
        if (mapping.hsl) vars.push(mapping.hsl);
    }

    for (const [, varName] of DERIVED_HEX_VARS) {
        vars.push(varName);
    }

    for (const [, varName] of DERIVED_HSL_VARS) {
        vars.push(varName);
    }

    return vars;
}

/**
 * Apply a custom theme by overriding CSS custom properties on :root.
 * Inline styles on documentElement have highest specificity, overriding design-tokens.css.
 */
export function applyCustomTheme(
    overrides: Partial<CustomThemeColors>,
    mode: 'light' | 'dark' = 'light'
): void {
    if (!overrides || Object.keys(overrides).length === 0) return;

    const root = document.documentElement;

    // Apply direct mappings
    for (const [key, value] of Object.entries(overrides) as [keyof CustomThemeColors, string][]) {
        if (!value) continue;
        const mapping = COLOR_VAR_MAP[key];
        if (!mapping) continue;

        root.style.setProperty(mapping.hex, value);
        if (mapping.hsl) {
            root.style.setProperty(mapping.hsl, hexToHSL(value));
        }
    }

    // Apply derived hex variables
    for (const [sourceKey, varName, transform] of DERIVED_HEX_VARS) {
        const sourceValue = overrides[sourceKey];
        if (!sourceValue) continue;
        if (varName === '--color-surface-elevated' && overrides.surfaceElevated) {
            continue;
        }
        root.style.setProperty(varName, transform(sourceValue));
    }

    // Apply derived HSL variables
    for (const [sourceKey, varName, transform] of DERIVED_HSL_VARS) {
        const sourceValue = overrides[sourceKey];
        if (!sourceValue) continue;
        if (varName === '--popover' && overrides.surfaceElevated) {
            continue;
        }
        if (varName === '--primary-foreground' && overrides.textOnCta) {
            continue;
        }
        root.style.setProperty(varName, hexToHSL(transform(sourceValue)));
    }

    // Store current mode for reference
    root.dataset.customTheme = mode;
}

/**
 * Remove all custom theme overrides, reverting to design-tokens.css defaults.
 */
export function clearCustomTheme(): void {
    const root = document.documentElement;

    for (const varName of getAllManagedVars()) {
        root.style.removeProperty(varName);
    }

    delete root.dataset.customTheme;
}

/**
 * Check if a custom theme is currently applied.
 */
export function hasCustomTheme(): boolean {
    return document.documentElement.dataset.customTheme !== undefined;
}

/**
 * Read a CSS custom property value from the computed style.
 */
export function getCSSVar(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// ---------------------------------------------------------------------------
// localStorage cache — enables blocking pre-render theme application
// ---------------------------------------------------------------------------

const LEGACY_THEME_CACHE_KEY = 'custom-theme-config';
const THEME_CACHE_PREFIX = 'theme:';
const THEME_MODE_KEY = 'app-theme-mode';

/**
 * localStorage key holding the id of the tenant whose theme was last
 * applied. Written whenever a tenant's theme is cached so the FOUC-guard
 * bootstrap script in index.html — which runs before React and cannot read
 * Redux/app state — knows which tenant-scoped cache slot to read.
 */
export const ACTIVE_TENANT_KEY = 'theme-active-tenant';

/**
 * EAA-1664: widened from one global cache slot to a tenant-scoped one so a
 * multi-tenant instance can cache (and instantly re-apply) a different
 * tenant's theme per session, keyed `theme:{tenantId}`. Callers with no
 * tenant context (single-tenant/instance-only deployments, or apps that
 * haven't resolved a tenant yet) fall back to the original global slot.
 */
function themeCacheKey(tenantId?: string | null): string {
    return tenantId ? `${THEME_CACHE_PREFIX}${tenantId}` : LEGACY_THEME_CACHE_KEY;
}

/**
 * Persist the current custom theme config to localStorage so the
 * blocking <script> in index.html can apply it before React mounts.
 */
// EAA-1679: mirrors themeValidation.ts's `sanitizeThemeConfig` hasAnything
// check — this used to only look at light/dark color keys, so a tenant that
// set *only* `skin`/`skinTokens`/`borderRadius`/`typography` (no color
// overrides) had its config treated as empty and deleted from the cache
// instead of persisted. That cache is what the embed iframe's `theme=` URL
// param is built from (IframeBlobWrapper.tsx), so a skin-only tenant theme
// never reached the content player at all.
function configHasAnything(config: CustomThemeConfig): boolean {
    return (
        Object.keys(config.light ?? {}).length > 0 ||
        Object.keys(config.dark ?? {}).length > 0 ||
        config.logoUrl !== undefined ||
        config.borderRadius !== undefined ||
        config.typography !== undefined ||
        config.skin !== undefined ||
        config.skinTokens !== undefined
    );
}

export function cacheCustomTheme(config: CustomThemeConfig | null, tenantId?: string | null): void {
    try {
        const key = themeCacheKey(tenantId);
        if (config && configHasAnything(config)) {
            localStorage.setItem(key, JSON.stringify(config));
        } else {
            localStorage.removeItem(key);
        }
        // EAA-1664 fix (Fable 5 review): only ever *set* the active-tenant
        // marker, never clear it, on a null tenantId — a caller that hasn't
        // resolved a tenant yet (e.g. a transient effect re-run) doesn't mean
        // no tenant is active, and this key may be shared across apps on the
        // same origin; clearing it here could wipe another app's just-written
        // state.
        if (tenantId) {
            localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
        }
    } catch {
        /* quota or private mode */
    }
}

/**
 * Read the cached custom theme config from localStorage.
 * Returns null if nothing is cached or the data is corrupt.
 */
export function getCachedCustomTheme(tenantId?: string | null): CustomThemeConfig | null {
    try {
        const raw = localStorage.getItem(themeCacheKey(tenantId));
        if (!raw) return null;
        return JSON.parse(raw) as CustomThemeConfig;
    } catch {
        return null;
    }
}

// Re-export the type needed by the inline script
import type { CustomThemeConfig } from './types';
export type { CustomThemeConfig as _InternalCustomThemeConfig };

/**
 * Self-contained blocking theme applier.
 * This is designed to be **copy-pasted** into a <script> tag in index.html
 * so it runs synchronously before the first paint.
 *
 * It is also exported as a function so it can be called from main.tsx
 * for apps that prefer a JS-only approach.
 */
export function applyThemeFromCache(): void {
    try {
        const mode = (localStorage.getItem(THEME_MODE_KEY) || 'light') as 'light' | 'dark';
        document.documentElement.setAttribute('data-theme', mode);

        const tenantId = localStorage.getItem(ACTIVE_TENANT_KEY);
        const raw = localStorage.getItem(themeCacheKey(tenantId));
        if (!raw) return;

        const config = JSON.parse(raw);
        const overrides = mode === 'dark' ? config.dark : config.light;
        if (!overrides || Object.keys(overrides).length === 0) return;

        // Inline hex→HSL (duplicate of hexToHSL, but must be self-contained)
        function h2hsl(hex: string): string {
            const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            if (!m) return '0 0% 0%';
            const r = parseInt(m[1], 16) / 255;
            const g = parseInt(m[2], 16) / 255;
            const b = parseInt(m[3], 16) / 255;
            const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
            const l = (mx + mn) / 2;
            if (mx === mn) return `0 0% ${Math.round(l * 100)}%`;
            const d = mx - mn;
            const s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
            let h = 0;
            if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            else if (mx === g) h = ((b - r) / d + 2) / 6;
            else h = ((r - g) / d + 4) / 6;
            return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
        }

        function dk(hex: string, pct: number): string {
            const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            if (!m) return hex;
            const f = 1 - pct / 100;
            return '#' + [1,2,3].map(i => Math.round(parseInt(m[i],16)*f).toString(16).padStart(2,'0')).join('');
        }

        function lt(hex: string, pct: number): string {
            const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            if (!m) return hex;
            const f = pct / 100;
            return '#' + [1,2,3].map(i => { const c = parseInt(m[i],16); return Math.round(c+(255-c)*f).toString(16).padStart(2,'0'); }).join('');
        }

        const root = document.documentElement.style;
        const MAP: Record<string, [string, string?]> = {
            primary: ['--color-primary'],
            cta: ['--color-cta', '--primary'],
            background: ['--color-background', '--background'],
            surface: ['--color-surface', '--card'],
            surfaceElevated: ['--color-surface-elevated', '--popover'],
            text: ['--color-text', '--foreground'],
            textMuted: ['--color-text-muted', '--muted-foreground'],
            textLight: ['--color-text-light'],
            // EAA-1804: mirrors COLOR_VAR_MAP above — keep the two in step.
            textOnCta: ['--color-text-on-cta', '--primary-foreground'],
            tableHeaderBg: ['--color-table-header-bg'],
            tableHeaderText: ['--color-table-header-text'],
            border: ['--color-border', '--border'],
            success: ['--color-success', '--success'],
            warning: ['--color-warning', '--warning'],
            error: ['--color-error', '--destructive'],
            info: ['--color-info', '--info'],
            sidebarSelectedBg: ['--color-sidebar-menu-selected-bg'],
            sidebarSelectedText: ['--color-sidebar-menu-selected-text'],
        };

        for (const [key, [hexVar, hslVar]] of Object.entries(MAP)) {
            const v = overrides[key];
            if (!v) continue;
            root.setProperty(hexVar, v);
            if (hslVar) root.setProperty(hslVar, h2hsl(v));
        }

        // Derived vars
        if (overrides.primary) {
            root.setProperty('--color-primary-hover', dk(overrides.primary, 12));
        }
        if (overrides.tableHeaderBg) {
            root.setProperty('--color-table-header-bg-hover', dk(overrides.tableHeaderBg, 3));
        }
        if (overrides.cta) {
            root.setProperty('--color-cta-hover', dk(overrides.cta, 12));
            root.setProperty('--ring', h2hsl(overrides.cta));
            root.setProperty('--secondary', h2hsl(lt(overrides.cta, 80)));
            root.setProperty('--secondary-foreground', h2hsl(dk(overrides.cta, 30)));
            root.setProperty('--accent', h2hsl(lt(overrides.cta, 85)));
            root.setProperty('--accent-foreground', h2hsl(dk(overrides.cta, 30)));
            if (!overrides.textOnCta) {
                root.setProperty('--primary-foreground', '0 0% 100%');
            }
        }
        if (overrides.background) {
            root.setProperty('--muted', h2hsl(lt(overrides.background, 3)));
            root.setProperty('--input', h2hsl(overrides.background));
        }
        if (overrides.surface && !overrides.surfaceElevated) {
            root.setProperty('--color-surface-elevated', overrides.surface);
            root.setProperty('--popover', h2hsl(overrides.surface));
        }
        if (overrides.text) {
            root.setProperty('--card-foreground', h2hsl(overrides.text));
            root.setProperty('--popover-foreground', h2hsl(overrides.text));
        }

        document.documentElement.dataset.customTheme = mode;
    } catch {
        /* silent — never break page load */
    }
}
